import type { NextRouter } from 'next/router';
import type { NextError } from 'next/dist/lib/is-error';
import { SortingState, SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import { IbhFilterKeys } from '@hub/libs/constants';
import type { Headers } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import type { IbhFilterValues } from '@hub/hooks/states/useIbhFilterValuesStore';
import type { DimensionKey, IbhDimensions, LifetimeParameters, ShapeDescriptor } from '@hub/bearing-hub/common/types';
import {
  DefaultServiceLifeForBearings,
  DefaultServiceLifeForBearingsForUS,
} from '@hub/bearing-hub/libs/BearingsLifetime/constants';
import {
  DEFAULT_QUANTITY,
  lifetimeConfigParams,
  ShapeDimensionsDefinitionForm,
} from '@hub/bearing-hub/common/constants';
import type { ParsedUrlQuery } from 'querystring';
import { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';

type FilterParams = {
  [key in IbhFilterKeys]?: string;
};

type DimensionsKeys = {
  [key in DimensionKey]?: string;
};

export type IbhQueryParams = {
  shape: string;
  unitSystem: string;
  sortingBy?: string;
  page: string;
  quantity: string;
  lifetimeParameters: string;
  categoryName?: string;
  edit?: string;
  artnr?: string;
} & FilterParams &
  DimensionsKeys;

export const queryParamsHandler = (() => {
  const filterEmptyValues = <T extends { [key: string]: string }>(params: T) =>
    Object.entries(params).reduce<T>((acc, [key, value]) => {
      if (!!value && value !== 'false') {
        acc[key as keyof T] = value as T[keyof T];
      }
      return acc;
    }, {} as T);

  const getDataFromParams = <T,>(queryParamsData: string): T => {
    if (!queryParamsData) {
      return {} as T;
    }

    const parts = queryParamsData.split('-');

    return parts.reduce((acc, part) => {
      const [key, value] = part.split(':');
      acc[key] = Number.isNaN(Number(value)) ? value : Number(value);
      return acc;
    }, {} as T);
  };

  const getDimensions = (filterValues: IbhFilterValues, selectedShape: ShapeDescriptor) => {
    const dimensions = filterValues.dimensions as IbhDimensions;
    return ShapeDimensionsDefinitionForm[selectedShape.shapeKey].reduce((acc: Record<string, string>, prev) => {
      acc[prev] = dimensions?.[prev as keyof IbhDimensions]?.toString() ?? '';
      return acc;
    }, {});
  };

  const getDimensionsParams = (filterValues: IbhFilterValues, selectedShape: ShapeDescriptor) => {
    const dimensionsWithoutEmptyValues = filterEmptyValues(getDimensions(filterValues, selectedShape));
    const dimensionsEntries = Object.entries(dimensionsWithoutEmptyValues);
    if (dimensionsEntries.length === 0) {
      return '';
    }
    return Object.entries(dimensionsWithoutEmptyValues)
      .map(([key, value]) => `${key}:${value}`)
      .join('-');
  };

  const getServiceLifeParams = (lifetimeParam: LifetimeParameters, unitSystem: IbhUnitSystem) =>
    Object.entries(lifetimeParam)
      .filter(([paramKey, paramValue]) => {
        const defaultValuesSource =
          unitSystem === IbhUnitSystem.Metric ? DefaultServiceLifeForBearings : DefaultServiceLifeForBearingsForUS;
        if (paramKey === lifetimeConfigParams.surfacePressure || paramKey === lifetimeConfigParams.load) {
          return false;
        }

        if (!(paramValue === 0 || !!paramValue)) {
          return false;
        }

        const defaultValue =
          paramKey === lifetimeConfigParams.force
            ? defaultValuesSource.load.force
            : (defaultValuesSource as Record<string, number>)[paramKey];

        return paramValue !== defaultValue;
      })
      .map(([key, value]) => `${key}:${value as string}`)
      .join('-');

  const getSortedQueryParams = (params: IbhQueryParams): IbhQueryParams => {
    const expectedOrder = [
      'metric',
      'quantity',
      'dimensions',
      'lifetimeParameters',
      'productionMethod',
      'material',
      'temperatureRangeMin',
      'temperatureRangeMax',
      'fdaCompliance',
      'eu102011Compliance',
      'dirtAndDust',
      'lowWaterAbsorption',
      'chemicals',
      'underwaterSplashwater',
      'electricallyConductive',
      'page',
      'sortingBy',
      'edit',
    ];

    const sortedEntries = Object.entries(params).sort(([aKey], [bKey]) => {
      const aIndex = expectedOrder.indexOf(aKey);
      const bIndex = expectedOrder.indexOf(bKey);
      return aIndex - bIndex;
    });

    return Object.fromEntries(sortedEntries) as IbhQueryParams;
  };

  const updateRouterQuery = (router: NextRouter, newQuery: IbhQueryParams) => {
    let query = newQuery;
    let hash = '';
    // apend query params after hash
    const { shape, categoryName, artnr, ...queryParams } = newQuery;
    query = {
      ...(shape && { shape }),
      ...(categoryName && { categoryName }),
      ...(artnr && { artnr }),
    } as IbhQueryParams;
    hash = new URLSearchParams(queryParams).toString();
    const currentHash = router.asPath.split('#')[1] || '';

    if (currentHash !== hash) {
      router
        .replace(
          {
            pathname: router.pathname,
            query,
            hash,
          },
          undefined,
          { shallow: true, scroll: false }
        )
        .catch((e: NextError) => {
          // workaround for https://github.com/vercel/next.js/issues/37362
          if (!e.cancelled) {
            throw e;
          }
        });
    }
  };

  const getUpdatedQueryParams = (
    selectedShape: ShapeDescriptor,
    unitSystem: IbhUnitSystem,
    currentSortingHeaderState: SortingState,
    currentSortingHeaderKey: Headers,
    page: number,
    quantityInputValue: number,
    filterValues: IbhFilterValues,
    lifetimeParameters: LifetimeParameters,
    locale: string,
    cartPositionEditParams?: string
  ): IbhQueryParams => {
    const newQuery: IbhQueryParams = {
      shape: selectedShape.shapePath,
      unitSystem,
      quantity: DEFAULT_QUANTITY.toString(),
      page: '1',
      sortingBy: currentSortingHeaderState,
      fdaCompliance: 'false',
      eu102011Compliance: 'false',
      dirtAndDust: 'false',
      lowWaterAbsorption: 'false',
      chemicals: 'false',
      underwaterSplashwater: 'false',
      electricallyConductive: 'false',
      dimensions: '',
      temperatureRangeMin: '',
      temperatureRangeMax: '',
      maxSurfacePressure: '',
      lifetimeParameters: '',
    };

    const updateSortingParams = () => {
      const defaultSortingBy =
        currentSortingHeaderState === SortingState.Ascending && currentSortingHeaderKey === 'price';

      if (defaultSortingBy) {
        newQuery.sortingBy = undefined;
      } else if (currentSortingHeaderState !== SortingState.NoSort) {
        newQuery.sortingBy = `${currentSortingHeaderState}-${currentSortingHeaderKey}`;
      } else {
        newQuery.sortingBy = `${currentSortingHeaderState}`;
      }
    };

    const updatePaginationParams = () => {
      if (page === 1) {
        newQuery.page = undefined;
      } else if (page && !Number.isNaN(page)) {
        newQuery.page = `${page}`;
      }
    };

    const updateQuantityParams = () => {
      newQuery.quantity =
        quantityInputValue >= 1 && quantityInputValue !== DEFAULT_QUANTITY ? `${quantityInputValue}` : undefined;
    };

    const updateMaterialFilterValueParams = () => {
      const filterMap = new Map<string, string | number | boolean | string[] | IbhDimensions>(
        Object.entries(filterValues)
      );
      filterMap.forEach((value, key) => {
        if (key !== IbhFilterKeys.DIMENSIONS) {
          newQuery[key as keyof FilterParams] = value.toString();
        }
      });
    };

    const updateDimensionsFilterValueParams = () => {
      newQuery.dimensions = getDimensionsParams(filterValues, selectedShape);
    };

    const updateServiceLifeParams = () => {
      newQuery.lifetimeParameters = getServiceLifeParams(lifetimeParameters, unitSystem);
    };

    const updateEditBasketParams = () => {
      newQuery.edit = cartPositionEditParams;
    };

    const updateUnitSystemParams = () => {
      if (unitSystem === IbhUnitSystem.Metric) {
        newQuery.unitSystem = SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? IbhUnitSystem.Metric : undefined;
      } else {
        newQuery.unitSystem = SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? undefined : IbhUnitSystem.Imperial;
      }
    };

    updateSortingParams();
    updatePaginationParams();
    updateQuantityParams();
    updateMaterialFilterValueParams();
    updateDimensionsFilterValueParams();
    updateServiceLifeParams();
    updateEditBasketParams();
    updateUnitSystemParams();

    const newQueryWithoutEmptyAndDefaultValues = filterEmptyValues(newQuery);

    return getSortedQueryParams(newQueryWithoutEmptyAndDefaultValues);
  };

  const createHashParam = (routerQuery: ParsedUrlQuery) => {
    const hash = new URLSearchParams();
    Object.keys(routerQuery).forEach(key => {
      hash.append(key, routerQuery[key] as string);
    });
    return hash;
  };

  return {
    updateRouterQuery,
    getUpdatedQueryParams,
    getServiceLifeParams,
    getDataFromParams,
    createHashParam,
  };
})();
